import { Attachment } from 'src/app/core/models/attachment.model';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, shareReplay, take, finalize, delay, catchError } from 'rxjs/operators';
import { UploadService } from 'src/app/core/services/upload.service';
import { untilDestroy } from '@ngrx-utils/store';

@Component({
  selector: 'app-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentComponent implements OnInit, OnDestroy {

  @Input()
  set attachment(attachment: Attachment) {
    this.attachment$.next(attachment);
    this.downloadURL$.next(attachment?.downloadURL);
    this.path$.next(attachment?.path);
    if (!attachment?.path) {
      this.showOriginalURL$.next(true);
    }
    if (!attachment) {
      this.downloadURL$.next(null);
      this.showOriginalURL$.next(false);
    }
  }

  @Input() placeholder = true;
  @Input() icon: string;
  @Input() progressType: 'progress-spinner' | 'progress-bar';
  @Input() diameter = 24;

  attachment$ = new BehaviorSubject<Attachment | null>(null);
  file$ = this.getFile();
  thumbnailURL$ = this.getThumbnailURL();
  previewURL$ = this.getPreviewURL();
  downloadURL$ = new BehaviorSubject<string | null>(null);
  path$ = new BehaviorSubject<string | null>(null);
  task$ = this.getTask();
  percentage$ = this.getPercentage();
  uploaded$ = new BehaviorSubject(false);
  showOriginalURL$ = new BehaviorSubject(false);

  @Output() uploaded = new EventEmitter<Attachment>();
  @Output() removed = new EventEmitter<string>();

  constructor(
    private uploadService: UploadService
  ) { }

  ngOnInit(): void {
    this.task$.pipe(
      switchMap(task => task.snapshotChanges().pipe(
        finalize(() => this.uploaded$.next(true))
      )),
      untilDestroy(this)
    ).subscribe();

    this.uploaded$.pipe(
      filter(uploaded => uploaded),
      switchMap(() => this.attachment$.pipe(take(1))),
      switchMap(attachmnet => this.uploadService.getDownloadURL(attachmnet.path).pipe(
        map(downloadURL => {
          return { downloadURL, path: attachmnet.path };
        })
      )),
      untilDestroy(this)
    ).subscribe(attachmnet => {
      this.downloadURL$.next(attachmnet.downloadURL);
      this.uploaded.emit(attachmnet);
    });
  }

  ngOnDestroy() {}

  removeAttachment() {
    if (!this.uploaded$.getValue()) {
      this.task$.pipe(
        take(1)
      ).subscribe(task => task.cancel());
    }
    const path = this.path$.getValue();
    this.uploadService.delete(path).pipe(
      take(1)
    ).subscribe(
      () => {},
      error => console.warn
    );
    this.removed.emit(path);
  }

  private getTask() {
    return this.attachment$.pipe(
      filter(attachment => !!attachment?.file && !attachment?.downloadURL),
      map(attachment => this.uploadService.upload(attachment.file, attachment.path))
    );
  }

  private getPercentage() {
    return this.task$.pipe(
      switchMap(task => task.percentageChanges())
    );
  }

  private getFile(): Observable<File> {
    return this.attachment$.pipe(
      filter(attachment => !!attachment?.file),
      map(attachment => attachment.file),
      shareReplay(1)
    );
  }

  private getPreviewURL(): Observable<string> {
    return this.file$.pipe(
      switchMap(file => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return fromEvent(reader, 'load');
      }),
      map((file: any) => file?.srcElement?.result)
    );
  }

  private getThumbnailURL(): Observable<string> {
    return this.attachment$.pipe(
      filter(attachment => !!attachment?.path),
      switchMap(attachment => this.uploadService.getThumbnailURL(attachment.path).pipe(
        catchError(err => {
          this.showOriginalURL$.next(true);
          return of(null);
        })
      ))
    );
  }

}
